import { InAppState } from "../base/InAppState";
import { Grid, html } from "gridjs";

// Registers the <platform-event-form> custom element used by the template.
import "../../components/event/form";

import template from "./PlatformEventListTemplate.html";
import { _lcs, locale, localizeCatalog } from "../../i18n";
import { gridLanguage } from "../../core/grid";

// German source strings that double as dictionary keys — a module constant is
// evaluated before I18n exists, so it is translated where it is read.
const STATUS_LABELS = {
    published : "Veröffentlicht",
    draft     : "Entwurf",
    cancelled : "Abgesagt",
    postponed : "Verschoben",
    archived  : "Archiviert"
};

/**
 * PlatformEventList — "Veranstaltungen": the platform's own event pool
 * (GET /event/platform), i.e. everything that belongs to no accommodation —
 * today that is what the external feeds import.
 *
 * Rows come from the compact admin payload, so a feed of a few thousand events
 * with a year of dates each is still one manageable response; the full record
 * is fetched when a row is opened for editing.
 *
 * Editing locks an event against the nightly import (see the form) — which is
 * why the lock is a visible column and not a hidden flag.
 */
export class PlatformEventList extends InAppState
{
    static ROUTE = '/events';
    static ID = 'platform-event-list';

    async enter()
    {
        this.app.view.render( this.app.view.pageContent, template );

        const root = this.app.view.pageContent;
        this.stateEl = root.querySelector( '[data-id="events-state"]' );
        this.truncatedEl = root.querySelector( '[data-id="truncated"]' );
        this.sourceFilter = root.querySelector( '[data-id="filter-source"]' );
        this.statusFilter = root.querySelector( '[data-id="filter-status"]' );
        this.rangeFilter = root.querySelector( '[data-id="filter-range"]' );
        this.duplicateFilter = root.querySelector( '[data-id="filter-duplicates"]' );
        this.pastFilter = root.querySelector( '[data-id="filter-past"]' );
        this.eventForm = root.querySelector( '[data-id="evp-form"]' );

        this.categoryToggle = root.querySelector( '[data-id="filter-category-toggle"]' );
        this.categoryPanel = root.querySelector( '[data-id="filter-category-panel"]' );
        this.categoryList = root.querySelector( '[data-id="filter-category-list"]' );
        this.categoryLabel = root.querySelector( '[data-id="filter-category-label"]' );

        this.events = [];
        this.eventById = new Map();
        this.sources = [];
        this.categories = [];
        this.selectedCategories = new Set();

        // Handed over by the source list ("Veranstaltungen" on a source card).
        this.selectedSourceId = this.app.pendingEventSourceId || "";
        this.app.pendingEventSourceId = null;

        this.addEventListener( this.stateEl, "click", ( e ) => this.onGridAction( e ) );
        this.addEventListener( this.eventForm, "platform-event-saved", () => this.loadEvents() );
        this.addEventListener( this.sourceFilter, "change", () => this.loadEvents() );
        this.addEventListener( this.statusFilter, "change", () => this.loadEvents() );
        this.addEventListener( this.rangeFilter, "change", () => this.loadEvents() );
        this.addEventListener( this.duplicateFilter, "change", () => this.loadEvents() );
        this.addEventListener( this.pastFilter, "change", () => this.loadEvents() );
        this.addEventListener( root.querySelector( '[data-id="btn-reload"]' ), "click", () => this.loadEvents() );

        this.bindCategoryFilter();

        await Promise.all( [ this.loadSources(), this.loadCategories() ] );
        await this.loadEvents();
    }

    /* --------------------------- Category multi-select ------------------------ */

    bindCategoryFilter()
    {
        this.addEventListener( this.categoryToggle, "click", ( e ) =>
        {
            e.stopPropagation();
            this.toggleCategoryPanel( this.categoryPanel.hidden );
        } );

        // Checking a box filters immediately; the panel stays open so several
        // categories can be picked in one go.
        this.addEventListener( this.categoryList, "change", ( e ) =>
        {
            const input = e.target.closest( "input[type=checkbox]" );
            if ( !input ) return;

            if ( input.checked ) this.selectedCategories.add( input.value );
            else this.selectedCategories.delete( input.value );

            this.updateCategoryLabel();
            this.loadEvents();
        } );

        this.addEventListener( this.categoryPanel.querySelector( '[data-id="filter-category-clear"]' ), "click", () =>
        {
            this.selectedCategories.clear();
            this.categoryList.querySelectorAll( "input[type=checkbox]" ).forEach( ( input ) => { input.checked = false; } );
            this.updateCategoryLabel();
            this.loadEvents();
        } );

        // Tracked, so InfrontJS removes them when the state exits.
        this.addEventListener( document, "click", ( e ) =>
        {
            if ( !this.categoryPanel.hidden && !this.categoryPanel.contains( e.target ) )
            {
                this.toggleCategoryPanel( false );
            }
        } );
        this.addEventListener( document, "keydown", ( e ) =>
        {
            if ( e.key === "Escape" ) this.toggleCategoryPanel( false );
        } );
    }

    toggleCategoryPanel( open )
    {
        this.categoryPanel.hidden = !open;
        this.categoryToggle.setAttribute( "aria-expanded", String( open ) );
    }

    // The category vocabulary is platform data (same catalog the edit form uses).
    async loadCategories()
    {
        try
        {
            const { result } = await this.app.api.get( "/event/catalog" );
            this.categories = ( result && +result.status === 200 && result.data && Array.isArray( result.data.categories ) )
                ? localizeCatalog( result.data.categories )
                : [];
        }
        catch ( e )
        {
            this.categories = [];
        }

        this.categoryList.innerHTML = this.categories.map( ( category ) =>
            '<label class="checkbox evp__multi-item">' +
                '<input type="checkbox" value="' + this._escape( category.key ) + '" />' +
                '<span class="checkbox__box"><i class="icon icon--check"></i></span>' +
                '<i class="icon icon--' + this._escape( category.icon || "calendar" ) + ' evp__multi-icon"></i>' +
                this._escape( category.title ) +
            '</label>'
        ).join( "" );

        this.updateCategoryLabel();
    }

    updateCategoryLabel()
    {
        const count = this.selectedCategories.size;

        if ( count === 0 )
        {
            this.categoryLabel.textContent = _lcs( "Alle Kategorien" );
        }
        else if ( count === 1 )
        {
            const key = Array.from( this.selectedCategories )[ 0 ];
            const category = this.categories.find( ( c ) => c.key === key );
            // Category titles come from the API catalog, not the dictionary.
            this.categoryLabel.textContent = category ? category.title : key;
        }
        else
        {
            this.categoryLabel.textContent = _lcs( "{count} Kategorien", { count } );
        }

        this.categoryToggle.classList.toggle( "evp__multi-toggle--active", count > 0 );
    }

    /* ------------------------------- Loading -------------------------------- */

    // Best-effort: the filter is a convenience, an unreachable source list must
    // not stop the events from loading.
    async loadSources()
    {
        try
        {
            const { result } = await this.app.api.get( "/eventsource" );
            this.sources = ( result && +result.status === 200 && Array.isArray( result.data ) ) ? result.data : [];
        }
        catch ( e )
        {
            this.sources = [];
        }

        this.sourceFilter.innerHTML = '<option value="">' + this._escape( _lcs( "Alle Quellen" ) ) + '</option>' +
            this.sources.map( ( source ) =>
                '<option value="' + this._escape( source.id ) + '">' + this._escape( source.name || source.key ) + '</option>'
            ).join( "" );

        this.sourceFilter.value = this.selectedSourceId || "";
    }

    async loadEvents()
    {
        this.showMessage( "calendar-days", _lcs( "Lade Veranstaltungen …" ), _lcs( "Einen Moment bitte." ) );
        this.truncatedEl.hidden = true;

        const query = [];
        if ( this.sourceFilter.value ) query.push( "source=" + encodeURIComponent( this.sourceFilter.value ) );
        if ( this.statusFilter.value ) query.push( "status=" + encodeURIComponent( this.statusFilter.value ) );
        if ( this.selectedCategories.size > 0 )
        {
            query.push( "category=" + encodeURIComponent( Array.from( this.selectedCategories ).join( "," ) ) );
        }
        if ( this.rangeFilter.value ) query.push( "days=" + encodeURIComponent( this.rangeFilter.value ) );
        if ( this.duplicateFilter.value !== "exclude" ) query.push( "duplicates=" + encodeURIComponent( this.duplicateFilter.value ) );
        if ( this.pastFilter.checked ) query.push( "upcoming=0" );

        let payload = null;
        try
        {
            const { result } = await this.app.api.get( "/event/platform" + ( query.length ? "?" + query.join( "&" ) : "" ) );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected /event/platform response." );
            }
            payload = result.data;
        }
        catch ( e )
        {
            console.error( e );
            this.showMessage(
                "triangle-alert",
                _lcs( "Veranstaltungen konnten nicht geladen werden" ),
                _lcs( "Bitte versuche es später erneut." )
            );
            return;
        }

        this.events = Array.isArray( payload.items ) ? payload.items : [];
        this.eventById = new Map();
        this.events.forEach( ( event ) => this.eventById.set( String( event.id ), event ) );

        // Never pretend a capped list is the whole list.
        if ( payload.isTruncated )
        {
            this.truncatedEl.textContent = _lcs(
                "Es werden die ersten {limit} Veranstaltungen angezeigt. Grenze die Auswahl über die Filter weiter ein.",
                { limit: payload.limit }
            );
            this.truncatedEl.hidden = false;
        }

        if ( this.events.length === 0 )
        {
            this.showMessage(
                "calendar",
                _lcs( "Keine Veranstaltungen gefunden" ),
                _lcs( "Für diese Auswahl liegen keine Daten vor. Der Import läuft über „Datenquellen“." )
            );
            return;
        }

        this.renderGrid();
    }

    renderGrid()
    {
        this._destroyGrid();
        this.stateEl.innerHTML = "";

        const rows = this.events.map( ( event ) => [
            event.title || "—",
            event.categoryTitle ? _lcs( event.categoryTitle ) : ( event.category || "—" ),
            this._dateText( event ),
            [ event.locationName, event.city ].filter( Boolean ).join( " · " ) || "—",
            this._sourceLabel( event ),
            String( event.status || "" ),
            String( event.id )
        ] );

        this.grid = new Grid( {
            columns: [
                {
                    name: _lcs( "Titel" ),
                    formatter: ( cell, row ) => html(
                        '<span class="admin-users__name" title="' + this._escape( cell ) + '">' +
                            this._markersHtml( row.cells[ 6 ] ? this.eventById.get( String( row.cells[ 6 ].data ) ) : null ) +
                            this._escape( cell ) +
                        '</span>'
                    )
                },
                _lcs( "Kategorie" ),
                _lcs( "Nächster Termin" ),
                {
                    name: _lcs( "Ort" ),
                    formatter: ( cell ) => html(
                        '<span class="admin-users__name" title="' + this._escape( cell ) + '">' + this._escape( cell ) + '</span>'
                    )
                },
                _lcs( "Quelle" ),
                {
                    name: _lcs( "Status" ),
                    formatter: ( cell ) => html(
                        '<span class="acc-status acc-status--' + this._escape( String( cell ).toLowerCase() ) + '">' +
                            this._escape( STATUS_LABELS[ cell ] ? _lcs( STATUS_LABELS[ cell ] ) : ( cell || "—" ) ) +
                        '</span>'
                    )
                },
                {
                    name: _lcs( "Aktionen" ),
                    sort: false,
                    formatter: ( cell ) =>
                    {
                        const edit = this._escape( _lcs( "Bearbeiten" ) );
                        return html(
                            '<div class="admin-users__actions">' +
                                '<button type="button" class="admin-users__action" data-action="edit" ' +
                                        'data-event-id="' + this._escape( cell ) + '" title="' + edit + '" aria-label="' + edit + '">' +
                                    '<i class="icon icon--pencil"></i><span>' + edit + '</span>' +
                                '</button>' +
                            '</div>'
                        );
                    }
                }
            ],
            data: rows,
            search: true,
            sort: true,
            pagination: { limit: 25, summary: true },
            className: { container: "admin-users__grid" },
            language: gridLanguage( _lcs( "Veranstaltungen" ), _lcs( "Keine Veranstaltungen gefunden" ) )
        } );

        this.grid.render( this.stateEl );
    }

    /**
     * Row markers: edited-by-hand (immune to the import) and recognised-as-a-
     * copy (hidden from guests). Both change what the record means, so they
     * belong next to the title rather than in a column nobody scans.
     */
    _markersHtml( event )
    {
        if ( !event )
        {
            return "";
        }

        let markers = "";
        if ( event.isLocked )
        {
            markers += '<i class="icon icon--lock evp__lock" title="' +
                this._escape( _lcs( "Manuell bearbeitet — wird beim Import nicht überschrieben" ) ) + '"></i> ';
        }
        if ( event.isDuplicate )
        {
            markers += '<i class="icon icon--layers evp__dupe" title="' +
                this._escape( _lcs( "Dublette — diese Veranstaltung kam bereits über eine andere Quelle und wird Gästen nicht angezeigt" ) ) + '"></i> ';
        }
        return markers;
    }

    /* -------------------------------- Actions -------------------------------- */

    onGridAction( e )
    {
        const btn = e.target.closest( "[data-action]" );
        if ( !btn || !this.stateEl.contains( btn ) ) return;

        if ( btn.getAttribute( "data-action" ) === "edit" )
        {
            this.openEditor( btn.getAttribute( "data-event-id" ) );
        }
    }

    /**
     * The table holds compact rows; the form needs the full record (long text,
     * location, dates), so it is fetched on demand.
     */
    async openEditor( eventId )
    {
        try
        {
            const { result } = await this.app.api.get( "/event/platform/" + eventId );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected event response." );
            }
            this.eventForm.openEdit( result.data, this._sourceName( result.data.eventSourceId ) );
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( _lcs( "Veranstaltung konnte nicht geladen werden." ), "error" );
        }
    }

    /* ------------------------------- Formatting ------------------------------ */

    _dateText( event )
    {
        const occurrence = event.nextOccurrence;
        const iso = occurrence ? occurrence.startsAt : event.startsAt;
        if ( !iso )
        {
            return _lcs( "Kein Termin" );
        }

        const date = new Date( iso );
        if ( isNaN( date.getTime() ) )
        {
            return "—";
        }

        // Weekday from Intl rather than from a dictionary entry: the platform
        // already knows the short name in every language we could ship.
        const day = date.toLocaleDateString( locale(), { weekday: "short" } ) + ", " +
            date.toLocaleDateString( locale(), { day: "2-digit", month: "2-digit", year: "numeric" } );

        const time = ( occurrence && occurrence.isAllDay ) || event.isAllDay
            ? _lcs( "ganztägig" )
            : _lcs( "{time} Uhr", {
                time : date.toLocaleTimeString( locale(), { hour: "2-digit", minute: "2-digit" } )
            } );

        const more = event.occurrenceCount > 1 ? " (+" + ( event.occurrenceCount - 1 ) + ")" : "";
        return day + " · " + time + more;
    }

    _sourceLabel( event )
    {
        if ( event.eventSourceId )
        {
            return this._sourceName( event.eventSourceId );
        }
        return event.source === "platform" ? "BELTURO" : ( event.sourceProvider || "—" );
    }

    _sourceName( eventSourceId )
    {
        const source = this.sources.find( ( s ) => String( s.id ) === String( eventSourceId ) );
        return source ? ( source.name || source.key ) : "—";
    }

    showMessage( icon, title, text )
    {
        this._destroyGrid();
        this.stateEl.innerHTML =
            '<div class="content__empty">' +
                '<span class="content__empty-icon"><i class="icon icon--' + icon + '"></i></span>' +
                '<h2></h2>' +
                '<p></p>' +
            '</div>';
        this.stateEl.querySelector( "h2" ).textContent = title;
        this.stateEl.querySelector( "p" ).textContent = text;
    }

    _destroyGrid()
    {
        if ( this.grid )
        {
            try { this.grid.destroy(); } catch ( e ) { /* DOM is replaced anyway */ }
            this.grid = null;
        }
    }

    async exit()
    {
        this._destroyGrid();
        await super.exit();
    }

    _escape( str )
    {
        return String( str === null || str === undefined ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    getContentHeaderData()
    {
        return {
            title: _lcs( "Veranstaltungen" ),
            subtitle: _lcs( "Regionale Veranstaltungen der Plattform" )
        };
    }
}
